'use client'

import { type ReactNode } from 'react'
import { cn } from '../lib/cn'

export interface HeroProps {
  /** Presentation title */
  title: string | ReactNode
  /** Optional subtitle */
  subtitle?: string
  /** Optional year badge */
  year?: string
  /** Client name displayed above title */
  clientName?: string
  /** Theme variant */
  theme?: 'dark' | 'light'
  /** Show scroll indicator */
  showScrollIndicator?: boolean
  /** Additional className */
  className?: string
}

/**
 * Hero Section Component
 *
 * Full-screen hero section for presentation introductions.
 * Automatically tracked with data-section-id="hero".
 *
 * @example
 * ```tsx
 * <Hero
 *   title="Digital Transformation"
 *   subtitle="A Strategic Proposal"
 *   clientName="ACME Corporation"
 *   year="2026"
 * />
 * ```
 */
export function Hero({
  title,
  subtitle,
  year,
  clientName,
  theme = 'dark',
  showScrollIndicator = true,
  className,
}: HeroProps) {
  const isDark = theme === 'dark'

  return (
    <section
      data-section-id="hero"
      data-section-label="Start"
      className={cn(
        'relative min-h-screen flex items-center justify-center overflow-hidden',
        isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900',
        className
      )}
    >
      {/* Year Badge */}
      {year && (
        <div className="absolute top-12 right-12 md:top-16 md:right-20 z-20">
          <span
            className={cn(
              'px-5 py-2.5 rounded-full text-sm font-medium',
              isDark
                ? 'bg-white/10 text-white border border-white/20'
                : 'bg-gray-900/10 text-gray-900 border border-gray-900/20'
            )}
          >
            {year}
          </span>
        </div>
      )}

      {/* Main Content */}
      <div className="relative z-10 text-center px-6 md:px-8 max-w-5xl mx-auto">
        {/* Client Name Label */}
        {clientName && (
          <p
            className={cn(
              'text-xs md:text-sm tracking-[0.3em] uppercase mb-8 md:mb-12',
              isDark ? 'text-white/50' : 'text-gray-900/50'
            )}
          >
            {clientName}
          </p>
        )}

        {/* Title */}
        <h1
          className={cn(
            'text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 md:mb-8',
            isDark ? 'text-white' : 'text-gray-900'
          )}
        >
          {title}
        </h1>

        {/* Subtitle */}
        {subtitle && (
          <h2
            className={cn(
              'text-xl md:text-2xl lg:text-3xl font-light',
              isDark ? 'text-white/70' : 'text-gray-900/70'
            )}
          >
            {subtitle}
          </h2>
        )}

        {/* Decorative line */}
        <div
          className={cn(
            'mt-12 md:mt-16 mx-auto w-16 h-px',
            isDark ? 'bg-white/30' : 'bg-gray-900/30'
          )}
        />
      </div>

      {/* Scroll Indicator */}
      {showScrollIndicator && (
        <div className="absolute bottom-8 md:bottom-12 left-1/2 -translate-x-1/2 z-20">
          <div
            className={cn(
              'w-6 h-10 border-2 rounded-full flex justify-center pt-2',
              isDark ? 'border-white/30' : 'border-gray-900/30'
            )}
          >
            <div
              className={cn(
                'w-1.5 h-1.5 rounded-full animate-bounce',
                isDark ? 'bg-white/60' : 'bg-gray-900/60'
              )}
            />
          </div>
        </div>
      )}
    </section>
  )
}
